/**
 * Web polyfill for React Native's `Alert.alert`.
 *
 * react-native-web ships an `Alert` whose `.alert()` is effectively a no-op:
 * the button list never renders and `onPress` never fires. Screens that gate an
 * action behind a confirm (e.g. TimeClock's "Start your shift now?") therefore
 * do nothing on web, and error alerts are invisible.
 *
 * We bridge it to the browser's native dialogs:
 *   - 0/1 button  → window.alert, then fire the single button's onPress.
 *   - 2+ buttons  → window.confirm; OK fires the primary (non-cancel) button,
 *                   Cancel fires the cancel button.
 *
 * Imported for side effects once at web startup (see index.web.js). Keep it
 * dependency-free so it can run before the app mounts.
 */
import { Alert } from 'react-native';

type AlertButton = {
  text?: string;
  onPress?: (value?: string) => void;
  style?: 'default' | 'cancel' | 'destructive';
};

const webAlert = (
  title?: string,
  message?: string,
  buttons?: AlertButton[]
): void => {
  const text = [title, message].filter(Boolean).join('\n\n');

  if (!buttons || buttons.length <= 1) {
    // eslint-disable-next-line no-alert
    window.alert(text);
    buttons?.[0]?.onPress?.();
    return;
  }

  const cancelButton = buttons.find((b) => b.style === 'cancel');
  const primaryButton =
    buttons.find((b) => b.style !== 'cancel') ?? buttons[buttons.length - 1];

  // eslint-disable-next-line no-alert
  const confirmed = window.confirm(text);
  if (confirmed) {
    primaryButton?.onPress?.();
  } else {
    cancelButton?.onPress?.();
  }
};

// Override the no-op RNW implementation. Cast through unknown — the RN types
// don't perfectly match our simplified signature but the runtime shape does.
(Alert as unknown as { alert: typeof webAlert }).alert = webAlert;
